import type {
  BiologicalSex,
  PatientBaseProfile,
  PatientMeasurementField,
  PatientMeasurementMetricType,
  PatientMeasurementRecord,
  PatientRecord,
} from '../types';
import { arePatientMeasurementFieldsComplete } from './measurementValidation';

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toOptionalString = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};

const toBiologicalSex = (value: unknown): BiologicalSex => {
  if (value === 'male' || value === 'female') {
    return value;
  }

  return null;
};

const toMetricType = (value: unknown): PatientMeasurementMetricType => {
  if (
    value === 'weight_kg' ||
    value === 'shoulder_cm' ||
    value === 'hips_cm' ||
    value === 'waist_cm' ||
    value === 'chest_cm' ||
    value === 'neck_cm' ||
    value === 'thigh_cm' ||
    value === 'calf_cm'
  ) {
    return value;
  }

  return null;
};

const normalizePatientBaseProfile = (
  raw: Record<string, unknown>
): PatientBaseProfile => {
  const nested =
    raw.profile != null && typeof raw.profile === 'object'
      ? (raw.profile as Record<string, unknown>)
      : {};

  const read = (snakeKey: string, camelKey: string) =>
    raw[snakeKey] ?? raw[camelKey] ?? nested[snakeKey] ?? nested[camelKey];

  return {
    biologicalSex: toBiologicalSex(read('biological_sex', 'biologicalSex')),
    dateOfBirth: toOptionalString(read('date_of_birth', 'dateOfBirth')),
    heightCm: toNumber(read('height_cm', 'heightCm')),
  };
};

export const normalizePatientRecord = (
  raw: unknown,
  fallbackCode?: string
): PatientRecord | null => {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const id = toNumber(record.id);

  if (id == null) {
    return null;
  }

  return {
    id,
    userId: toNumber(record.user_id ?? record.userId),
    code:
      toOptionalString(record.code) ??
      fallbackCode ??
      `Utente ${String(id).padStart(4, '0')}`,
    createdAt: toOptionalString(record.created_at ?? record.createdAt),
    updatedAt: toOptionalString(record.updated_at ?? record.updatedAt),
    profile: normalizePatientBaseProfile(record),
  };
};

const normalizeMeasurementField = (
  raw: unknown,
  index: number
): PatientMeasurementField | null => {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const label = toOptionalString(record.label);
  const value = toOptionalString(record.value) ?? '';

  if (label == null) {
    return null;
  }

  return {
    id:
      toOptionalString(record.id) ?? toNumber(record.id) ?? `${label}-${index}`,
    label,
    value,
    unit: toOptionalString(record.unit),
    metricType: toMetricType(record.metric_type ?? record.metricType),
    position: toNumber(record.position) ?? index,
    scope:
      record.scope === 'base' || record.scope === 'extra'
        ? record.scope
        : undefined,
  };
};

const BACKEND_MEASUREMENT_COLUMNS: Array<{
  metricType: Exclude<PatientMeasurementMetricType, null>;
  label: string;
  unit: string;
  keys: string[];
}> = [
  {
    metricType: 'weight_kg',
    label: 'Peso',
    unit: 'kg',
    keys: ['weight_kg', 'weightKg'],
  },
  {
    metricType: 'shoulder_cm',
    label: 'Spalle',
    unit: 'cm',
    keys: ['shoulder_cm', 'shoulderCm'],
  },
  {
    metricType: 'hips_cm',
    label: 'Fianchi',
    unit: 'cm',
    keys: ['hips_cm', 'hipsCm'],
  },
  {
    metricType: 'waist_cm',
    label: 'Vita',
    unit: 'cm',
    keys: ['waist_cm', 'waistCm'],
  },
  {
    metricType: 'chest_cm',
    label: 'Petto',
    unit: 'cm',
    keys: ['chest_cm', 'chestCm'],
  },
  {
    metricType: 'neck_cm',
    label: 'Collo',
    unit: 'cm',
    keys: ['neck_cm', 'neckCm'],
  },
  {
    metricType: 'thigh_cm',
    label: 'Coscia',
    unit: 'cm',
    keys: ['thigh_cm', 'thighCm'],
  },
  {
    metricType: 'calf_cm',
    label: 'Polpacci',
    unit: 'cm',
    keys: ['calf_cm', 'calfCm'],
  },
];

const readRecordNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = toNumber(record[key]);
    if (value != null) {
      return value;
    }
  }

  return null;
};

const normalizeBackendBodyMeasurementRow = (
  record: Record<string, unknown>,
  patientId: number
): PatientMeasurementRecord => {
  const fields = BACKEND_MEASUREMENT_COLUMNS.flatMap((definition, index) => {
    const value = readRecordNumber(record, definition.keys);

    if (value == null) {
      return [];
    }

    return [
      {
        id: `${definition.metricType}-${index}`,
        label: definition.label,
        value: String(value),
        unit: definition.unit,
        metricType: definition.metricType,
        position: index,
        scope: 'base' as const,
      },
    ];
  });

  const rawExtra =
    record.extra_fields_json ?? record.extraFieldsJson ?? record.extraFields;
  let extraFields: PatientMeasurementField[] = [];

  if (typeof rawExtra === 'string' && rawExtra.trim() !== '') {
    try {
      const parsed = JSON.parse(rawExtra) as unknown;
      if (Array.isArray(parsed)) {
        const mapped: Array<PatientMeasurementField | null> = parsed.map(
          (entry, index) => {
            if (
              entry == null ||
              typeof entry !== 'object' ||
              Array.isArray(entry)
            ) {
              return null;
            }
            const item = entry as Record<string, unknown>;
            const label = toOptionalString(item.label)?.trim() ?? '';
            if (label === '') {
              return null;
            }
            return {
              id: `extra-${index}`,
              label,
              value: String(item.value ?? ''),
              unit: toOptionalString(item.unit) ?? null,
              metricType: null,
              position: BACKEND_MEASUREMENT_COLUMNS.length + index,
              scope: 'extra' as const,
            };
          }
        );
        extraFields = mapped.filter(
          (field): field is PatientMeasurementField => field != null
        );
      }
    } catch {
      extraFields = [];
    }
  } else if (Array.isArray(rawExtra)) {
    const mapped: Array<PatientMeasurementField | null> = rawExtra.map(
      (entry, index) => {
        if (
          entry == null ||
          typeof entry !== 'object' ||
          Array.isArray(entry)
        ) {
          return null;
        }
        const item = entry as Record<string, unknown>;
        const label = toOptionalString(item.label)?.trim() ?? '';
        if (label === '') {
          return null;
        }
        return {
          id: `extra-${index}`,
          label,
          value: String(item.value ?? ''),
          unit: toOptionalString(item.unit) ?? null,
          metricType: null,
          position: BACKEND_MEASUREMENT_COLUMNS.length + index,
          scope: 'extra' as const,
        };
      }
    );
    extraFields = mapped.filter(
      (field): field is PatientMeasurementField => field != null
    );
  }

  return {
    id: toNumber(record.id) ?? undefined,
    patientId: toNumber(record.patient_id ?? record.patientId) ?? patientId,
    notes:
      toOptionalString(record.notes ?? record.note ?? record.general_notes) ??
      '',
    biologicalSex: toBiologicalSex(
      record.biological_sex ?? record.biologicalSex
    ),
    heightCm: readRecordNumber(record, ['height_cm', 'heightCm']),
    dateOfBirth: toOptionalString(
      record.date_of_birth ?? record.dateOfBirth
    ),
    fields: [...fields, ...extraFields],
    createdAt: toOptionalString(record.created_at ?? record.createdAt),
    updatedAt: toOptionalString(record.updated_at ?? record.updatedAt),
  };
};

export const normalizePatientMeasurementRecord = (
  raw: unknown,
  patientId: number
): PatientMeasurementRecord | null => {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const rawFields = Array.isArray(record.fields) ? record.fields : null;

  if (rawFields != null) {
    return {
      id: toNumber(record.id) ?? undefined,
      patientId: toNumber(record.patient_id ?? record.patientId) ?? patientId,
      notes:
        toOptionalString(record.notes ?? record.note ?? record.general_notes) ??
        '',
      biologicalSex: toBiologicalSex(
        record.biological_sex ?? record.biologicalSex
      ),
      heightCm: readRecordNumber(record, ['height_cm', 'heightCm']),
      dateOfBirth: toOptionalString(
        record.date_of_birth ?? record.dateOfBirth
      ),
      fields: rawFields
        .map((field, index) => normalizeMeasurementField(field, index))
        .filter((field): field is PatientMeasurementField => field != null)
        .sort((left, right) => left.position - right.position),
      createdAt: toOptionalString(record.created_at ?? record.createdAt),
      updatedAt: toOptionalString(record.updated_at ?? record.updatedAt),
    };
  }

  const hasBackendMeasurementColumns = BACKEND_MEASUREMENT_COLUMNS.some(
    definition => definition.keys.some(key => record[key] != null)
  );

  if (!hasBackendMeasurementColumns) {
    return null;
  }

  return normalizeBackendBodyMeasurementRow(record, patientId);
};

const readMeasurementFieldValue = (
  values: PatientMeasurementRecord,
  metricType: Exclude<PatientMeasurementMetricType, null>
) => {
  const field = values.fields.find(entry => entry.metricType === metricType);
  return toNumber(field?.value);
};

export const buildPatientBodyMeasurementPayload = (
  values: PatientMeasurementRecord
) => {
  const extraFields = values.fields
    .filter(
      field => (field.scope ?? 'extra') === 'extra' && field.label.trim() !== ''
    )
    .map((field, index) => ({
      label: field.label.trim(),
      value: field.value.trim(),
      unit: field.unit?.trim() || null,
      position: field.position ?? index,
    }));

  const payload = {
    biologicalSex: toBiologicalSex(values.biologicalSex),
    heightCm: toNumber(values.heightCm),
    dateOfBirth:
      typeof values.dateOfBirth === 'string' && values.dateOfBirth.trim() !== ''
        ? values.dateOfBirth.trim()
        : null,
    weightKg: readMeasurementFieldValue(values, 'weight_kg'),
    shoulderCm: readMeasurementFieldValue(values, 'shoulder_cm'),
    hipsCm: readMeasurementFieldValue(values, 'hips_cm'),
    waistCm: readMeasurementFieldValue(values, 'waist_cm'),
    chestCm: readMeasurementFieldValue(values, 'chest_cm'),
    neckCm: readMeasurementFieldValue(values, 'neck_cm'),
    thighCm: readMeasurementFieldValue(values, 'thigh_cm'),
    calfCm: readMeasurementFieldValue(values, 'calf_cm'),
    notes: values.notes.trim() === '' ? null : values.notes.trim(),
    extraFields,
  };

  const hasAnyValue = arePatientMeasurementFieldsComplete(values.fields);

  return hasAnyValue ? payload : null;
};
